// DOM renderer for the Shroud landing chat.
//
// XSS posture: every piece of model output, tool result, and citation URL
// is rendered through `textContent` or DOM `createElement` calls. We never
// hand untrusted strings to `innerHTML`. Citation links parse markdown-
// style `[text](url)` out of the assistant content and build proper anchor
// elements with `textContent` for the visible text.
//
// The widget mounts onto the existing `#hchat`, `#hchatFeed`, `#hchatInput`,
// and `#hchatSend` skeleton in the landing template, plus the static
// `.hchat__example` buttons. It expands the hero section in place when the
// conversation gets going, matching the locked UX decision.

import type { ToolCallTrace, ModelEntry } from './types.js';

/** Markdown-style link: [text](url). */
const CITE_REGEX = /\[([^\]]+)\]\(([^)]+)\)/g;

export class ChatUI {
  private chat: HTMLElement;
  private feed: HTMLElement;
  private input: HTMLTextAreaElement;
  private sendBtn: HTMLElement;
  private examples: HTMLElement | null;
  private currentBubble: HTMLElement | null = null;
  private thinkingIndicator: HTMLElement | null = null;
  private modelSelect: HTMLSelectElement | null = null;
  private streaming = false;

  constructor() {
    this.chat = document.getElementById('hchat') as HTMLElement;
    this.feed = document.getElementById('hchatFeed') as HTMLElement;
    this.input = document.getElementById('hchatInput') as HTMLTextAreaElement;
    this.sendBtn = document.getElementById('hchatSend') as HTMLElement;
    this.examples = this.chat.querySelector('.hchat__examples');
  }

  /** Wire up event handlers. The submit callback receives trimmed input. */
  setup(onSubmit: (text: string) => void): void {
    const submit = () => {
      const text = this.input.value.trim();
      if (!text || this.streaming) return;
      this.input.value = '';
      onSubmit(text);
    };

    this.sendBtn.addEventListener('click', submit);
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        submit();
      }
    });

    this.chat.querySelectorAll('.hchat__example').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.input.value = (btn as HTMLElement).dataset.ex ?? '';
        submit();
      });
    });
  }

  /** Expand the chat feed (triggered on first user message). */
  open(): void {
    this.chat.classList.add('is-open');
  }

  /** Switch to the larger expanded layout once the feed grows. */
  expand(): void {
    this.chat.classList.add('is-expanded');
  }

  /** Add a user message bubble. */
  addUserMessage(text: string): void {
    this.open();
    const bubble = document.createElement('div');
    bubble.className = 'hchat__bubble hchat__bubble--me';
    bubble.textContent = text;
    this.feed.appendChild(bubble);
    this.scrollToBottom();
  }

  /**
   * Mark the start of an assistant turn. Shows a "Thinking…" placeholder
   * for inference latency. The actual bubble is created lazily on the
   * first non-empty content chunk so a round that emits only a tool call
   * (stripped to empty content by the polyfill response transform) does
   * not leave an empty cursor bubble in the feed.
   */
  startAssistantMessage(): void {
    this.currentBubble = null;
    this.streaming = true;
    this.showThinking();
  }

  /** Show a "Thinking…" placeholder while waiting for the first chunk. */
  private showThinking(): void {
    if (this.thinkingIndicator) return;
    const el = document.createElement('div');
    el.className = 'hchat__thinking';
    const spinner = document.createElement('span');
    spinner.className = 'hchat__spinner';
    el.appendChild(spinner);
    el.appendChild(document.createTextNode(' Thinking…'));
    this.feed.appendChild(el);
    this.thinkingIndicator = el;
    this.scrollToBottom();
  }

  /** Remove the "Thinking…" placeholder if present. */
  private hideThinking(): void {
    if (!this.thinkingIndicator) return;
    this.thinkingIndicator.remove();
    this.thinkingIndicator = null;
  }

  /** Update the streaming AI bubble with new content (full content so far). */
  updateStreamingContent(displayContent: string): void {
    this.hideThinking();
    if (!this.currentBubble) {
      // Lazy creation on first content chunk.
      const bubble = document.createElement('div');
      bubble.className = 'hchat__bubble hchat__bubble--ai';
      this.feed.appendChild(bubble);
      this.currentBubble = bubble;
    }
    // During streaming we use plain textContent. Citations render at the
    // end so partial markdown does not turn into broken anchors.
    this.currentBubble.textContent = displayContent;
    const cursor = document.createElement('span');
    cursor.className = 'hchat__cursor';
    this.currentBubble.appendChild(cursor);
    this.scrollToBottom();
  }

  /**
   * Finalize the AI bubble: drop the cursor and re-render with citations
   * converted to clickable anchors. No-ops when the round produced only
   * tool calls and no bubble was lazily created.
   */
  endAssistantMessage(finalContent: string): void {
    this.hideThinking();
    if (!this.currentBubble) {
      this.streaming = false;
      return;
    }
    this.currentBubble.textContent = '';
    this.renderContentWithCitations(this.currentBubble, finalContent);
    this.streaming = false;
    this.currentBubble = null;
    this.scrollToBottom();

    if (this.feed.children.length > 6) {
      this.expand();
    }
  }

  /**
   * Render text with markdown-style `[text](url)` patterns converted to
   * `<a class="hchat__cite">` anchors. Uses createElement and textContent
   * exclusively — no innerHTML — to keep model output XSS-safe.
   */
  private renderContentWithCitations(container: HTMLElement, text: string): void {
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    const regex = new RegExp(CITE_REGEX.source, 'g');
    let matched = false;

    while ((match = regex.exec(text)) !== null) {
      matched = true;
      if (match.index > lastIndex) {
        container.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
      }

      const linkText = match[1];
      const linkUrl = match[2];
      const anchor = document.createElement('a');
      anchor.className = 'hchat__cite';
      anchor.href = linkUrl;
      anchor.target = '_blank';
      anchor.rel = 'noopener';
      anchor.textContent = linkText;
      container.appendChild(anchor);

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      container.appendChild(document.createTextNode(text.slice(lastIndex)));
    }

    if (!matched) {
      container.textContent = text;
    }
  }

  /** Add an inline tool trace block (collapsed by default). */
  addToolTrace(trace: ToolCallTrace): HTMLDetailsElement {
    const details = document.createElement('details');
    details.className = 'hchat__trace';

    const summary = document.createElement('summary');
    const icon = document.createElement('span');
    icon.className = 'hchat__trace-icon';
    icon.textContent = '⚙'; // gear
    summary.appendChild(icon);

    const label = document.createTextNode(
      trace.error
        ? ` ${trace.name} — failed: ${trace.error.split(':')[0]}`
        : ` ${trace.name} — ${trace.elapsedMs}ms`,
    );
    summary.appendChild(label);
    details.appendChild(summary);

    const body = document.createElement('div');
    body.className = 'hchat__trace-body';

    const argsLabel = document.createElement('div');
    argsLabel.className = 'hchat__trace-label';
    argsLabel.textContent = 'Arguments:';
    body.appendChild(argsLabel);

    const argsEditor = document.createElement('textarea');
    argsEditor.className = 'hchat__trace-editor';
    argsEditor.value = JSON.stringify(trace.args, null, 2);
    argsEditor.rows = Math.min(Object.keys(trace.args).length + 2, 8);
    body.appendChild(argsEditor);

    const rerunBtn = document.createElement('button');
    rerunBtn.className = 'hchat__trace-rerun';
    rerunBtn.textContent = 'Re-run Tool';
    rerunBtn.dataset.traceId = trace.id;
    rerunBtn.dataset.toolName = trace.name;
    body.appendChild(rerunBtn);

    const resultEl = document.createElement('pre');
    resultEl.className = trace.error ? 'hchat__trace-error' : 'hchat__trace-result';
    resultEl.textContent = trace.error
      ? trace.error
      : JSON.stringify(trace.result, null, 2);
    body.appendChild(resultEl);

    details.appendChild(body);
    this.feed.appendChild(details);
    this.scrollToBottom();
    return details;
  }

  /** Show a "tool is running" placeholder; remove once the trace lands. */
  addToolRunning(name: string): HTMLElement {
    this.hideThinking();
    const el = document.createElement('div');
    el.className = 'hchat__trace-running';
    const spinner = document.createElement('span');
    spinner.className = 'hchat__spinner';
    el.appendChild(spinner);
    el.appendChild(document.createTextNode(` ${name} — running...`));
    this.feed.appendChild(el);
    this.scrollToBottom();
    return el;
  }

  /** Remove a transient indicator (the running placeholder). */
  removeElement(el: HTMLElement): void {
    el.remove();
  }

  /** Show the low-confidence indicator banner. */
  addLowConfidenceBanner(): void {
    const banner = document.createElement('div');
    banner.className = 'hchat__low-conf';
    const icon = document.createElement('span');
    icon.textContent = 'ⓘ'; // info
    banner.appendChild(icon);
    banner.appendChild(
      document.createTextNode(' Low retrieval confidence — answer may not be reliable'),
    );
    this.feed.appendChild(banner);
    this.scrollToBottom();
  }

  /** Render an error inline. Reuses the active streaming bubble if any. */
  addError(message: string): void {
    if (this.currentBubble) {
      this.currentBubble.textContent = message;
      this.streaming = false;
      this.currentBubble = null;
    } else {
      const bubble = document.createElement('div');
      bubble.className = 'hchat__bubble hchat__bubble--ai';
      bubble.textContent = message;
      this.feed.appendChild(bubble);
    }
    this.scrollToBottom();
  }

  /** Build the model selector dropdown inside the chat bar. */
  createModelSelector(
    models: ModelEntry[],
    currentModel: string,
    onChange: (model: string) => void,
  ): void {
    const container = document.createElement('div');
    container.className = 'hchat__model-select';

    const select = document.createElement('select');
    select.setAttribute('aria-label', 'Select model');
    for (const m of models) {
      const opt = document.createElement('option');
      opt.value = m.model_id;
      opt.textContent = m.model_id;
      if (m.model_id === currentModel) opt.selected = true;
      select.appendChild(opt);
    }
    select.addEventListener('change', () => onChange(select.value));
    container.appendChild(select);
    this.modelSelect = select;

    const bar = this.chat.querySelector('.hchat__bar');
    if (bar) {
      bar.insertBefore(container, this.input);
    }
  }

  /** Add the "Powered by Shroud SDK" footer link. */
  addPoweredByFooter(): void {
    const footer = document.createElement('div');
    footer.className = 'hchat__powered';
    const link = document.createElement('a');
    link.href = 'https://github.com/AlphaTONCapital/shroud-sdk-ts';
    link.target = '_blank';
    link.rel = 'noopener';
    link.textContent = 'Powered by Shroud SDK';
    footer.appendChild(link);
    this.chat.appendChild(footer);
  }

  private scrollToBottom(): void {
    this.feed.scrollTop = this.feed.scrollHeight;
  }
}
